import _ from "lodash";
import {
  useState,
  useEffect,
  SetStateAction
} from "react";
import { toast } from "react-toastify";

import {
  CreateUpdateFeaturedProduct,
  FeaturedProductsRequestName,
  useFeaturedProductsApi,
  BulkUpdateFeaturedProductsParams
} from "../api/featured-products.api";

export interface UseFeaturedProductsReturn {
  isLoading: boolean;
  featuredProducts: CreateUpdateFeaturedProduct[] | [];
  setCurrentFeaturedProducts: React.Dispatch<SetStateAction<[] | CreateUpdateFeaturedProduct[]>>;
  bulkUpdateFeaturedProductsReq: (data: BulkUpdateFeaturedProductsParams) => void;
  getFeaturedProductsReq: (data: null) => void;
}

/**
 * Custom hook used to manage local featured products state as well as all API interactions
 */
const useFeaturedProducts = (): UseFeaturedProductsReturn => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ getFeaturedProductsRes, getFeaturedProductsReq ] = useFeaturedProductsApi(FeaturedProductsRequestName.GET_FEATURED_PRODUCTS);
  const [ bulkUpdateFeaturedProductsRes, bulkUpdateFeaturedProductsReq ] = useFeaturedProductsApi(FeaturedProductsRequestName.BULK_UPDATE_FEATURED_PRODUCTS);
  const [ currentFeaturedProducts, setCurrentFeaturedProducts ] = useState<CreateUpdateFeaturedProduct[] | []>([]);

  // update local state on update API response
  useEffect(() => {
    if (bulkUpdateFeaturedProductsRes.data) {
      setCurrentFeaturedProducts(bulkUpdateFeaturedProductsRes.data);
      toast.success("Successfully updated featured products.");
    }

    if (bulkUpdateFeaturedProductsRes.error) {
      toast.error(bulkUpdateFeaturedProductsRes.error.message || "Unable to update featured products.");
    }
  }, [ bulkUpdateFeaturedProductsRes ]);

  // update local state when getting featured products
  useEffect(() => {
    if (getFeaturedProductsRes.data) {
      // ensure order by priority then sanitise priority values
      setCurrentFeaturedProducts(_.map(_.orderBy(getFeaturedProductsRes.data, featuredProduct => featuredProduct.priority), (featuredProduct, index) => ({
        ...featuredProduct,
        priority: index + 1
      })));
    }

    if (getFeaturedProductsRes.error) {
      toast.error(getFeaturedProductsRes.error.message || "Unable to get featured products.");
    }
  }, [ getFeaturedProductsRes ]);

  // set loading state if any of the requests status updated
  useEffect(() => {
    setIsLoading(getFeaturedProductsRes.pending || bulkUpdateFeaturedProductsRes.pending);
  }, [ getFeaturedProductsRes, bulkUpdateFeaturedProductsRes ]);

  return {
    isLoading,
    featuredProducts: currentFeaturedProducts,
    setCurrentFeaturedProducts,
    bulkUpdateFeaturedProductsReq,
    getFeaturedProductsReq
  };
};

export default useFeaturedProducts;
